import { AppServices, SymbolType } from 'network'
import { doubleMSocketService, exchangeSocketService } from '../socket/ExchangeSocketService'
import { useBaseInfoStore } from './use-base-info'
import { useEnvStore } from './use-env'
import { useUserInfoStore } from './use-user-info'
import { useChangeLanguage } from '../hooks/use-change-language'
import { SocketEventName } from '../socket/SocketService'
import { useWebSocketStore } from './use-web-socket'
import { useExchangeInfoStore } from './use-exchange-info'
import { deepClone, requestIdleCallbackFrame } from 'utils'
import { useStaticTableDataStore } from '..'

export type CollectCoinParams = {
  symbolId: SymbolId
  swapType?: SymbolType
}

export const useQuoteStore = defineStore('quote', () => {
  const baseInfoStore = useBaseInfoStore()
  const envStore = useEnvStore()
  const searchKeyword = ref('')
  const staticTableDataStore = useStaticTableDataStore()

  /** 币币交易行情加载状态 */
  const loadingMarketM = ref(true)
  /** 合约交易行情加载状态 */
  const loadingContractM = ref(true)
  /** 自选列表 */
  const collectSymbolIds = ref<SymbolId[]>([])
  const userInfoStore = useUserInfoStore()
  /** 处理自选加载状态 */
  const collectLoadingMap = ref<Record<SymbolId, boolean>>({})

  /** 全量的币币交易行情数据 */
  const allSpotList = ref<SocketResponse.TradingPairData[]>([])

  /** 全量的合约交易行情数据 */
  const allContractList = ref<SocketResponse.TradingPairData[]>([])

  /** 推荐币种列表 */
  const recommendSymbolMap = ref<Record<SymbolId, SocketResponse.TradingPairData>>({})

  const exchangeInfoStore = useExchangeInfoStore()

  useWebSocketStore()

  const getCollectList = async () => {
    if (!userInfoStore.getToken()) return
    const res = await AppServices.getCollectList()
    collectSymbolIds.value = res.data?.digital?.map((item) => Number(item)) || []
  }

  /** 刷新自选列表 */
  const refreshCollectList = async (symbolId: SymbolId) => {
    // 为了视图快速渲染，在刷新自选列表接口时，可以先行处理自选数据
    const index = collectSymbolIds.value.indexOf(symbolId)
    if (index !== -1) {
      collectSymbolIds.value.splice(index, 1)
    } else {
      collectSymbolIds.value.push(symbolId)
    }
    getCollectList()
  }

  useChangeLanguage(getCollectList)

  const subDoubleM = () => {
    doubleMSocketService.emit('Topic.sub', {
      type: 'doubleM',
      app_id: envStore.appId,
    })
  }

  const subMarketM = () => {
    exchangeSocketService.emit('Topic.sub', {
      type: 'marketM',
      app_id: envStore.appId,
    })
  }

  const subQuotes = async () => {
    subMarketM()
    subDoubleM()
  }

  useChangeLanguage(subQuotes)

  /** 监听币币交易行情数据 */
  const onListenMarketM = (cb: (res: SocketResponse.MarketMResponse) => void) => {
    const listenCb = (res: SocketResponse.MarketMResponse) => {
      requestIdleCallbackFrame(() => {
        res.data.sort(sortSpotList)
        exchangeSocketService.setMarketQuoteWithCache(res.data)
      })
      loadingMarketM.value = false
      cb(res)
    }
    exchangeSocketService.offMessage<SocketResponse.MarketMResponse>(SocketEventName.MarketM, listenCb)
    exchangeSocketService.onMessage<SocketResponse.MarketMResponse>(SocketEventName.MarketM, listenCb)
  }

  /** 监听合约交易行情数据 */
  const onListenContractM = (cb: (res: SocketResponse.ContractMResponse) => void) => {
    const listenCb = (res: SocketResponse.ContractMResponse) => {
      doubleMSocketService.setContractQuoteWithCache(JSON.parse(JSON.stringify(res.data)))
      loadingContractM.value = false
      cb(res)
    }
    doubleMSocketService.offMessage<SocketResponse.ContractMResponse>(SocketEventName.DoubleM, listenCb)
    doubleMSocketService.onMessage<SocketResponse.ContractMResponse>(SocketEventName.DoubleM, listenCb)
  }

  /** 获取交易对的名字 */
  const getSymbolName = (symbolId: SymbolId) => {
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(symbolId)
    if (!data) return ''
    return data.name.toUpperCase()?.split('_')
  }

  const getSpotSymbolName = (symbolId: SymbolId) => {
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(symbolId)
    if (!data) return ''
    return data.name.toUpperCase()?.replace('_', '/')
  }

  const getSpotFromSymbolName = (symbolId: SymbolId) => {
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(symbolId)
    if (!data) return ''
    return data.name.toUpperCase()?.split('_')[0]
  }

  const getDisplaySymbolName = (symbolId: SymbolId) => {
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(symbolId)
    if (!data) return ''
    return staticTableDataStore.getCoinNameByCoinId(data.coinFrom)
  }

  const getContractDisplaySymbolName = (symbolId: SymbolId) => {
    const data = exchangeInfoStore.getContractSymbolInfoBySymbolId(symbolId)
    if (!data) return ''
    return staticTableDataStore.getCoinNameByCoinId(data.baseItemId)
  }

  /** 现货根据关键词过滤交易对 */
  const filterByKeyword = (item: SocketResponse.TradingPairData) => {
    const keyword = searchKeyword.value?.trim().toLowerCase()
    if (!keyword) return true

    const name = getSpotSymbolName(item.symbolId)?.split('/')[0] ?? []

    if (name) {
      return name.toLowerCase().includes(keyword)
    }

    // 安全处理 symbolId 为 number 的情况
    return String(item.symbolId).toLowerCase().includes(keyword)
  }

  /** 现货根据关键词过滤交易对 */
  const filterContractByKeyword = (item: SocketResponse.TradingPairData) => {
    const keyword = searchKeyword.value?.trim().toLowerCase()
    if (!keyword) return true

    const name = getContractSymbolName(item.symbolId)?.split('/')[0] ?? []

    if (name) {
      return name.toLowerCase().includes(keyword)
    }

    // 安全处理 symbolId 为 number 的情况
    return String(item.symbolId).toLowerCase().includes(keyword)
  }

  /** 通过交易对名字排序 */
  const handleSortMethodBySymbolName = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const nameA = getSymbolName(a.symbolId)?.[0] || ''
    const nameB = getSymbolName(b.symbolId)?.[0] || ''
    return nameA.localeCompare(nameB)
  }

  /** 通过最新价格排序 */
  const handleSortMethodByLastPrice = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const priceA = getLastPrice(a.symbolId)
    const priceB = getLastPrice(b.symbolId)
    if (priceA === '--' || priceB === '--') return 0
    return priceA.subtractStr(priceB).toNumber()
  }

  /** 获取币币的24H成交量 */
  const getLastVolume = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithSpotList(symbolId)
    if (!symbol) return '--'
    return symbol.currency.vol.formatNumberWithCommas()
  }

  /** 获取合约的24H成交量 */
  const getContractLastVolume = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithContractList(symbolId)
    if (!symbol) return '--'
    return symbol.currency.vol.formatNumberWithCommas()
  }

  /** 通过24H成交量排序 */
  const handleSortMethodByLastVolume = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const volumeA = getLastVolume(a.symbolId)
    const volumeB = getLastVolume(b.symbolId)
    return volumeA.subtractStr(volumeB).toNumber()
  }

  /** 通过合约的24H涨跌幅排序 */
  const handleSortMethodByContractLastPriceChange = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const priceChangeA = getContractLastPriceChange(a.symbolId)
    const priceChangeB = getContractLastPriceChange(b.symbolId)
    return priceChangeA.subtractStr(priceChangeB).toNumber()
  }

  /** 通过24H涨跌幅排序 */
  const handleSortMethodByLastPriceChange = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const priceChangeA = getLastPriceChange(a.symbolId)
    const priceChangeB = getLastPriceChange(b.symbolId)
    return priceChangeA.subtractStr(priceChangeB).toNumber()
  }

  /** 通过24H最高价排序 */
  const handleSortMethodByHigh = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const highA = a.currency.high
    const highB = b.currency.high
    return highA.subtractStr(highB).toNumber()
  }

  /** 通过合约的24H最高价排序 */
  const handleSortMethodByContractHigh = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const highA = a.currency.high
    const highB = b.currency.high
    return highA.subtractStr(highB).toNumber()
  }

  /** 通过24H最低价排序 */
  const handleSortMethodByLow = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const lowA = a.currency.low
    const lowB = b.currency.low
    return lowA.subtractStr(lowB).toNumber()
  }

  /** 通过合约的24H最低价排序 */
  const handleSortMethodByContractLow = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const lowA = a.currency.low
    const lowB = b.currency.low
    return lowA.subtractStr(lowB).toNumber()
  }

  const getLast24v = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithSpotList(symbolId)
    if (!symbol) return '--'
    return symbol.currency['24v']
  }

  const getContractLast24v = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithContractList(symbolId)
    if (!symbol) return '--'
    return symbol.currency['24v']
  }

  /** 通过24小时交易额排序 */
  const handleSortMethodByLast24v = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const volumeA = getLast24v(a.symbolId)
    const volumeB = getLast24v(b.symbolId)
    if (volumeA === '--' || volumeB === '--') return 0
    return volumeA.subtractStr(volumeB).toNumber()
  }

  /** 通过合约的24小时交易额排序 */
  const handleSortMethodByContractLast24v = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const volumeA = getContractLast24v(a.symbolId)
    const volumeB = getContractLast24v(b.symbolId)
    if (volumeA === '--' || volumeB === '--') return 0
    return volumeA.subtractStr(volumeB).toNumber()
  }

  /** 处理自选加载状态 */
  const handleCollectCoin = async (
    data: CollectCoinParams,
    options?: {
      addSuccess?: () => void
      removeSuccess?: () => void
    },
  ) => {
    const isCollect = collectSymbolIds.value.includes(data.symbolId)
    collectLoadingMap.value[data.symbolId] = true
    try {
      if (isCollect) {
        const res = await AppServices.removeCollect({
          symbolId: data.symbolId,
          swapType: data.swapType || SymbolType.Spot,
        })
        if (res.status === 200) {
          options?.removeSuccess?.()
          refreshCollectList(data.symbolId)
        }
      } else {
        const res = await AppServices.addCollect({
          symbolId: data.symbolId,
          swapType: data.swapType || SymbolType.Spot,
        })
        if (res.status === 200) {
          options?.addSuccess?.()
          refreshCollectList(data.symbolId)
        }
      }
    } catch (error) {
      console.log('error', error)
    } finally {
      collectLoadingMap.value[data.symbolId] = false
    }
  }

  const fetchCoinInfo = async (symbolId: SymbolId) => {
    const res = await AppServices.getCoinInfo({ swapSymbolId: symbolId })
    return res.data
  }

  const sortSpotList = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const dataA = baseInfoStore.getCoinStaticItemDataBySymbolId(a.symbolId)
    const dataB = baseInfoStore.getCoinStaticItemDataBySymbolId(b.symbolId)

    // 如果不存在dataA, 则将dataA排在后面
    if (!dataA) return 1
    // 如果不存在dataB, 则将dataB排在后面
    if (!dataB) return -1

    // 升序排列

    return dataA.orderBy - dataB.orderBy
  }

  /** 监听币币交易行情数据 */
  const listenMarketM = () => {
    exchangeSocketService.getMarketQuoteWithCache().then((res) => {
      allSpotList.value = res
      if (res.length > 0) {
        loadingMarketM.value = false
      }
      onListenMarketM((res) => {
        const list = res.data
        list.sort(sortSpotList)
        allSpotList.value = deepClone(list)
      })
    })
  }

  /** 监听合约交易行情数据 */
  const listenContractM = () => {
    doubleMSocketService.getContractQuoteWithCache().then((res) => {
      allContractList.value = res
      if (res.length > 0) {
        loadingContractM.value = false
      }
    })
    onListenContractM((res) => {
      const list = res.data
      allContractList.value = list
    })
  }

  /** 获取币币的最新价 */
  const getLastPrice = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithSpotList(symbolId)
    if (!symbol) return '--'
    return symbol.currency.last
  }

  const getSymbolBySymbolIdWithSpotList = (symbolId: SymbolId) => {
    return allSpotList.value.find((item) => item.symbolId === symbolId)
  }

  const getSymbolBySymbolIdWithContractList = (symbolId: SymbolId) => {
    return allContractList.value.find((item) => item.symbolId === symbolId)
  }

  /** 获取币币的最新法币价格 */
  const getLastCurrencyPrice = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithSpotList(symbolId)
    if (!symbol) return '--'
    const currency = (userInfoStore.currentToCurrency || 'usd') as keyof ApiResponse.OtcCoinRate

    const price = symbol.currency[currency as keyof SocketResponse.Currency] || symbol.usd
    return price.formatNumberWithCommas()
  }

  /** 获取币币的24H涨跌幅 */
  const getLastPriceChange = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithSpotList(symbolId)
    if (!symbol) return '--'
    return symbol.currency.priceChangePercent
  }

  const getContractLastPriceChange = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithContractList(symbolId)
    if (!symbol) return '--'
    return symbol.currency.priceChangePercent
  }

  const getContractLastCurrencyPrice = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithContractList(symbolId)
    if (!symbol) return '--'
    const currency = (userInfoStore.currentToCurrency || 'usd') as keyof ApiResponse.OtcCoinRate

    const price = symbol.currency[currency as keyof SocketResponse.Currency] || symbol.usd
    return price.formatNumberWithCommas()
  }

  const getContractLastPrice = (symbolId: SymbolId) => {
    const symbol = getSymbolBySymbolIdWithContractList(symbolId)
    if (!symbol) return '--'
    return symbol.currency.last.formatNumberWithCommas()
  }

  const listenRecommendMarketM = () => {
    listenMarketM()
  }

  const listenRecommendContractM = () => {
    doubleMSocketService.getContractQuoteWithCache().then((list) => {
      if (list.length > 0) {
        allContractList.value = list
      }
    })
  }

  const listenRecommendSymbol = () => {
    listenRecommendMarketM()
    listenRecommendContractM()
  }

  /** 根据交易对ID获取币种图标 */
  const getCoinIconBySymbolId = (symbolId: SymbolId) => {
    const data = baseInfoStore.getCoinStaticItemDataBySymbolId(symbolId)
    if (!data) return ''
    return baseInfoStore.getCoinInfoByItemId(data?.coinFrom)?.itemDetail.coinUrl
  }

  const getContractSymbolName = (symbolId: SymbolId) => {
    const symbol = exchangeInfoStore.getContractSymbolName(symbolId)
    if (!symbol) return ''
    return symbol
  }

  const handleDefaultSortMethod = (a: SocketResponse.TradingPairData, b: SocketResponse.TradingPairData) => {
    const dataA = baseInfoStore.getCoinStaticItemDataBySymbolId(a.symbolId)
    const dataB = baseInfoStore.getCoinStaticItemDataBySymbolId(b.symbolId)
    if (!dataA || !dataB) return 0
    return dataA.orderBy - dataB.orderBy
  }

  onMounted(() => {
    doubleMSocketService.onReconnect(() => {
      subDoubleM()
    })
    exchangeSocketService.onReconnect(() => {
      subMarketM()
    })
  })

  return {
    /** 全局socket服务 */
    exchangeSocketService,
    /** 币币交易行情加载状态 */
    loadingMarketM,
    /** 合约交易行情加载状态 */
    loadingContractM,
    /** 开启监听币币交易行情数据 */
    onListenMarketM,
    /** 开启监听合约交易行情数据 */
    onListenContractM,
    /** 自选列表 */
    collectSymbolIds,
    /** 刷新自选列表 */
    refreshCollectList,
    /** 处理自选加载状态 */
    handleCollectCoin,
    /** 自选加载状态 */
    collectLoadingMap,
    /** 搜索关键词 */
    searchKeyword,
    /** 获取交易对的名字 */
    getSymbolName,
    /** 根据关键词过滤交易对 */
    filterByKeyword,
    /** 通过交易对名字排序 */
    handleSortMethodBySymbolName,
    /** 通过最新价格排序 */
    handleSortMethodByLastPrice,
    /** 通过24H涨跌幅排序 */
    handleSortMethodByLastPriceChange,
    /** 获取交易对信息 */
    fetchCoinInfo,
    /** 执行监听币币交易行情数据 */
    listenMarketM,
    /** 全量的币币交易行情数据 */
    allSpotList,
    /** 监听推荐交易对 */
    listenRecommendSymbol,
    /** 推荐交易对 */
    recommendSymbolMap,
    /** 根据交易对ID获取币种图标 */
    getCoinIconBySymbolId,
    /** 获取币币的交易对名称 */
    getSpotSymbolName,
    /** 获取币币的最新价 */
    getLastPrice,
    /** 获取币币的24H涨跌幅 */
    getLastPriceChange,
    /** 获取币币的最新法币价格 */
    getLastCurrencyPrice,
    /** 获取币币的24H成交量 */
    getLastVolume,
    /** 通过24H成交量排序 */
    handleSortMethodByLastVolume,
    /** 全量的合约交易行情数据 */
    allContractList,
    /** 执行监听合约交易行情数据 */
    listenContractM,
    /** 获取合约的最新价 */
    getContractLastPrice,
    /** 获取合约的最新法币价格 */
    getContractLastCurrencyPrice,
    /** 获取合约的24H涨跌幅 */
    getContractLastPriceChange,
    /** 获取合约的24H成交量 */
    getContractLastVolume,
    /** 获取合约的交易对名称 */
    getContractSymbolName,
    /** 获取合约的交易对信息 */
    getSymbolBySymbolIdWithContractList,
    /** 获取币币的交易对显示名称 */
    getDisplaySymbolName,
    getSpotFromSymbolName,
    /** 默认排序 */
    handleDefaultSortMethod,
    /** 通过24小时交易额排序 */
    handleSortMethodByLast24v,
    /** 通过24H最高价排序 */
    handleSortMethodByHigh,
    /** 通过24H最低价排序 */
    handleSortMethodByLow,
    /** 合约交易名称查询 */
    filterContractByKeyword,
    /** 获取合约的交易对显示名称 */
    getContractDisplaySymbolName,
    /** 通过合约的24H涨跌幅排序 */
    handleSortMethodByContractLastPriceChange,
    /** 通过合约的24H最高价排序 */
    handleSortMethodByContractHigh,
    /** 通过合约的24H最低价排序 */
    handleSortMethodByContractLow,
    /** 通过合约的24小时交易额排序 */
    handleSortMethodByContractLast24v,
  }
})
